import { IMAGES } from "../data/content";
import { SectionHeader, CTABand } from "./Home";
import { Compass, Eye, Gem, HeartHandshake } from "lucide-react";

const VALUES = [
  { icon: Gem, title: "Precision", text: "Every finding traced to an original record. No assumptions, no shortcuts." },
  { icon: Eye, title: "Transparency", text: "Clear reports, honest timelines, and red flags stated plainly." },
  { icon: HeartHandshake, title: "Discretion", text: "Your matters remain strictly confidential, always." },
  { icon: Compass, title: "Stewardship", text: "We treat every client's transaction as if the land were our own." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeader
              eyebrow="About Terraa & Title"
              title="Founded on a simple belief: land deserves the truth."
            />
            <div className="mt-8 space-y-6 text-base font-light text-gray-600 leading-relaxed">
              <p>
                Terraa & Title was born from years spent inside registry offices, revenue departments, and courtrooms —
                watching good people lose fortunes to defects a proper title search would have caught.
              </p>
              <p>
                Today we combine old-school records expertise with modern process discipline, delivering title searches,
                land records retrieval, and due diligence that professionals stake their opinions on.
              </p>
              <p className="font-accent italic text-lg text-[#113835]">
                "We exist so that no one signs for land they don't truly understand."
              </p>
            </div>
          </div>
          <div className="relative">
            <img src={IMAGES.office} alt="Terraa & Title office" className="w-full h-[520px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-[#C1A74A] text-[#113835] p-8 max-w-xs hidden md:block">
              <p className="font-heading text-sm font-semibold uppercase tracking-widest">Our Promise</p>
              <p className="mt-2 text-sm font-light">Source-verified findings, delivered on the date we commit.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#113835] tt-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="tt-eyebrow mb-4">Our Mission</p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium text-white tracking-tight leading-snug">
              To make every property transaction transparent, verified, and dispute-proof.
            </h2>
          </div>
          <div>
            <p className="tt-eyebrow mb-4">Our Vision</p>
            <h2 className="font-heading text-2xl md:text-4xl font-medium text-white tracking-tight leading-snug">
              A market where clear title is the standard — <span className="font-accent italic text-[#F4D387]">not the exception.</span>
            </h2>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="What Guides Us" title="Values written into every report." className="mb-16" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="tt-card p-8">
                <v.icon size={28} strokeWidth={1} className="text-[#C1A74A]" />
                <h3 className="font-heading text-base font-medium text-[#113835] mt-5">{v.title}</h3>
                <p className="mt-3 text-sm text-gray-600 font-light leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="promise-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="The Terraa Promise" title="We will never sell you false certainty." className="mb-12" />
          <div className="space-y-6 text-base font-light text-gray-600 leading-relaxed">
            <p>Land records in India carry decades of history — legacy mutations, missing links, spelling variations, and informal inheritances. So we are clear about one thing:</p>
            <div className="space-y-5 border-l-2 border-[#C1A74A] pl-8 py-2">
              <p><strong className="text-[#113835] font-medium">When something is clear, we say it plainly.</strong> Straightforward findings are stated without hedging.</p>
              <p><strong className="text-[#113835] font-medium">When something is ambiguous, we say that too.</strong> We flag uncertainty openly and recommend deeper review instead of glossing over it.</p>
              <p><strong className="text-[#113835] font-medium">AI assists; experts decide.</strong> Our AI reads vernacular records and flags early risks — but complex cases always go to experienced examiners, advocates, and surveyors.</p>
              <p><strong className="text-[#113835] font-medium">Every case makes us sharper.</strong> Resolved edge cases feed back into our methodology, strengthening every report that follows.</p>
            </div>
            <p className="font-accent italic text-xl text-[#113835]">
              "We will be honest about what we know, careful about what we don't, and committed to improving with every parcel we examine."
            </p>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
